import { useTheme } from '@/components/ThemeProvider';
import { measureDistance } from '@/utils/drawing';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';
import { Spinner } from '../Spinner';

const polygonColor = '#FF0000';
const polygonStrokeOpacity = 0.7;
export type Polygon = number[][];
export type PolygonData = {
  polygon: Polygon;
  name: string;
};
interface PolygonOverlayProps {
  mediaUrl?: string;
  placeholderImage?: boolean;
  loading?: boolean;
  isVideo?: boolean;
  canEdit?: boolean;
  polygonData: PolygonData[] | undefined;
  onPolygonsChange?: (polygons: PolygonData[]) => void;
  onUpdateDrawingPolygon?: (polygon: Polygon) => void;
  isDrawingPolygon?: boolean;
}

export interface PolygonOverlayHandle {
  closePolygon: () => void;
}

export const PolygonOverlay = forwardRef<
  PolygonOverlayHandle,
  PolygonOverlayProps
>(
  (
    {
      mediaUrl,
      polygonData,
      loading,
      isVideo,
      canEdit,
      onPolygonsChange,
      onUpdateDrawingPolygon,
      isDrawingPolygon = false,
    },
    ref,
  ) => {
    const { theme } = useTheme();
    const imagePlaceholderPath = `/img-placeholder-${theme}.svg`;
    const [imageSize, setImageSize] = useState<{
      width: number;
      height: number;
    } | null>(null);

    const [draggingPoint, setDraggingPoint] = useState<{
      polygonIndex: number;
      pointIndex: number;
    } | null>(null);

    const [drawingPolygon, setDrawingPolygon] = useState<Polygon>([]);
    const [polygons, setPolygons] = useState<PolygonData[]>([]);
    const [debouncePolygons, setDebouncePolygons] = useState<PolygonData[]>();
    const imgRef = useRef<HTMLImageElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const drawing = useRef<boolean>(false);
    const [debouncedPolygonsChange] = useDebounce(debouncePolygons, 300);
    const drawingPolygonIndex = (polygons ?? []).findIndex(
      (polygon) => polygon.polygon.length === 0,
    );

    const closePolygon = () => {
      const normalizedNewPolygon = drawingPolygon;
      const newPolygons = [...polygons];
      newPolygons[drawingPolygonIndex] = {
        polygon: normalizedNewPolygon,
        name: '',
      };
      setPolygons?.(newPolygons);
      onPolygonsChange?.(newPolygons);
      setDrawingPolygon([]);
      drawing.current = false;
    };

    useImperativeHandle(ref, () => ({
      closePolygon,
    }));

    useEffect(() => {
      if (!polygonData) {
        return;
      }
      setPolygons(polygonData);
    }, [polygonData]);

    useEffect(() => {
      if (!debouncedPolygonsChange) {
        return;
      }
      onPolygonsChange?.(debouncedPolygonsChange);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedPolygonsChange]);

    const onLoad = () => {
      if (imgRef.current) {
        setImageSize({
          width: imgRef.current?.naturalWidth,
          height: imgRef.current?.naturalHeight,
        });
      } else if (videoRef.current) {
        setImageSize({
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight,
        });
      }
    };

    useEffect(() => {
      onUpdateDrawingPolygon?.(drawingPolygon);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawingPolygon]);

    useEffect(() => {
      setDrawingPolygon([]);
    }, [isDrawingPolygon]);

    // Convert normalized polygons to pixel polygons
    const pixelPolygons =
      imageSize &&
      (polygons ?? []).map((polygon) =>
        polygon.polygon.map(([x, y]) => [
          x * imageSize.width,
          y * imageSize.height,
        ]),
      );

    // Convert drawing polygon to pixel coordinates
    const pixelDrawingPolygon =
      imageSize && drawingPolygon.length > 0
        ? drawingPolygon.map(([x, y]) => [
            x * imageSize.width,
            y * imageSize.height,
          ])
        : [];

    const shouldCapturePointerEvents = Boolean(canEdit || isDrawingPolygon);

    const handleMouseDown = (
      event: React.MouseEvent<SVGGElement | SVGCircleElement>,
      polygonIndex: number,
      pointIndex: number,
    ) => {
      event.preventDefault();
      drawing.current = true;
      setDraggingPoint({ polygonIndex, pointIndex });
    };

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || !imageSize) return;
      if (!drawing.current) return;

      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();
      const normalizedX = (e.clientX - rect.left) / rect.width;
      const normalizedY = (e.clientY - rect.top) / rect.height;

      // Clamp values between 0 and 1 (normalized)
      const x = Math.max(0, Math.min(1, normalizedX));
      const y = Math.max(0, Math.min(1, normalizedY));

      if (draggingPoint) {
        // Update existing polygon point
        const newPolygons = polygons ?? [];
        newPolygons[draggingPoint.polygonIndex].polygon[
          draggingPoint.pointIndex
        ] = [x, y];
        setPolygons([...polygons]);
        setDebouncePolygons?.(newPolygons);
      }
    };

    const handleMouseUp = () => {
      setDraggingPoint(null);
      if (drawing.current) {
        drawing.current = false;
      }
    };

    const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
      if (!(drawing.current || isDrawingPolygon)) return;

      if (!svgRef.current) return;

      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();
      const normalizedX = (e.clientX - rect.left) / rect.width;
      const normalizedY = (e.clientY - rect.top) / rect.height;

      const x = Math.max(0, Math.min(1, normalizedX));
      const y = Math.max(0, Math.min(1, normalizedY));
      const newPoint: [number, number] = [x, y];
      // Check if clicking near the last point (to close polygon)
      const pixelNewPoint: [number, number] = [
        x * imageSize!.width,
        y * imageSize!.height,
      ];
      if (
        drawingPolygon.length > 2 &&
        measureDistance(pixelNewPoint, pixelDrawingPolygon[0]).isWithinTolerance
      ) {
        closePolygon();
        return;
      } else if (
        drawingPolygon.length > 0 &&
        measureDistance(
          pixelNewPoint,
          pixelDrawingPolygon[pixelDrawingPolygon.length - 1],
          20,
        ).isWithinTolerance
      ) {
        // Skip if too close to the previous point
        return;
      } else {
        // Add new point
        setDrawingPolygon([...drawingPolygon, newPoint]);
      }
    };

    return (
      <div className="flex min-h-9 flex-col select-none">
        <div className="relative h-full w-full">
          {loading && (
            <div className="absolute top-1/2 left-1/2 z-50 mt-7 h-16 -translate-x-1/2 -translate-y-1/2">
              <Spinner className="text-muted-foreground h-6" />
            </div>
          )}
          <div className="relative h-full w-full">
            {!mediaUrl ? (
              <div className="text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 truncate text-center text-sm italic">
                No Image.
              </div>
            ) : isVideo ? (
              <video
                ref={videoRef}
                onLoadedMetadata={onLoad}
                src={mediaUrl ? mediaUrl : undefined}
                preload="metadata"
                controls
              />
            ) : (
              <img
                ref={imgRef}
                onLoad={onLoad}
                className="text-muted-foreground block w-full select-none"
                draggable={false}
                src={mediaUrl ? mediaUrl : imagePlaceholderPath}
                alt="Device image"
              />
            )}
          </div>
          {imageSize && (pixelPolygons || pixelDrawingPolygon.length > 0) && (
            <svg
              ref={svgRef}
              className={`absolute top-0 left-0 h-full w-full select-none ${shouldCapturePointerEvents ? '' : 'pointer-events-none'}`}
              width={imageSize.width}
              height={imageSize.height}
              viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={handleSvgClick}
              onDragStart={(event) => event.preventDefault()}
            >
              {/* Existing polygons */}
              {pixelPolygons &&
                pixelPolygons.map((polygon, polygonIndex) => {
                  const label = polygons ? polygons[polygonIndex]?.name : '';
                  const fontSize = 24;
                  const paddingX = 12;
                  const paddingY = 6;
                  const textWidth = Math.max(
                    (label?.length ?? 0) * fontSize * 0.6,
                    fontSize,
                  );
                  const textHeight = fontSize;
                  const labelWidth = textWidth + paddingX * 2;
                  const labelHeight = textHeight + paddingY * 2;

                  const hasVertices = polygon.length > 0;

                  let anchorVertex: [number, number] | null = null;
                  if (hasVertices) {
                    anchorVertex = polygon.reduce<[number, number]>(
                      (t, point) => {
                        const bestScore = t[0] + t[1];
                        const candidateScore = point[0] + point[1];
                        return candidateScore < bestScore
                          ? (point as [number, number])
                          : t;
                      },
                      polygon[0] as [number, number],
                    );
                  }

                  let labelTransform: string | null = null;
                  let labelXOffset = 0;
                  let labelYOffset = 0;
                  const labelMargin = 8;
                  if (anchorVertex) {
                    const anchorX = anchorVertex[0];
                    const anchorY = anchorVertex[1];

                    // Prefer positioning above the vertex; fall back below if it would overflow.
                    const aboveOffset = -(labelHeight + labelMargin);
                    const belowOffset = labelMargin;
                    const fitsAbove = anchorY + aboveOffset >= labelMargin;
                    labelYOffset = fitsAbove ? aboveOffset : belowOffset;

                    const top = anchorY + labelYOffset;
                    const bottom = top + labelHeight;
                    const maxBottom = imageSize.height - labelMargin;
                    if (bottom > maxBottom) {
                      labelYOffset -= bottom - maxBottom;
                    }
                    if (anchorY + labelYOffset < labelMargin) {
                      labelYOffset += labelMargin - (anchorY + labelYOffset);
                    }

                    // Prefer positioning to the right; shift left if it would overflow.
                    const rightEdge = anchorX + labelXOffset + labelWidth;
                    const maxRight = imageSize.width - labelMargin;
                    if (rightEdge > maxRight) {
                      labelXOffset -= rightEdge - maxRight;
                    }
                    if (anchorX + labelXOffset < labelMargin) {
                      labelXOffset += labelMargin - (anchorX + labelXOffset);
                    }

                    labelTransform = `translate(${anchorX}, ${anchorY})`;
                  }

                  return (
                    <g key={`polygon-${polygonIndex}`}>
                      {/* Polygon outline */}
                      <polygon
                        points={polygon.map(([x, y]) => `${x},${y}`).join(' ')}
                        fill="none"
                        stroke={polygonColor}
                        strokeOpacity={polygonStrokeOpacity}
                        strokeWidth={6}
                        pointerEvents={canEdit ? 'auto' : 'none'}
                      />

                      {labelTransform ? (
                        <g
                          pointerEvents="none"
                          transform={labelTransform}
                          className="select-none"
                        >
                          <rect
                            x={labelXOffset}
                            y={labelYOffset}
                            width={labelWidth}
                            height={labelHeight}
                            fill="rgba(0, 0, 0, 0.6)"
                            rx={6}
                            ry={6}
                          />
                          <text
                            x={labelXOffset + paddingX}
                            y={labelYOffset + labelHeight / 2}
                            textAnchor="start"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fontWeight="600"
                            fill="white"
                            className="select-none"
                          >
                            {label}
                          </text>
                        </g>
                      ) : null}

                      {/* Draggable corner points (only in edit mode) */}
                      {canEdit &&
                        polygon.map(([x, y], pointIndex) => (
                          <g
                            key={`${polygonIndex}-${pointIndex}`}
                            className="cursor-move hover:opacity-80"
                            onMouseDown={(event) =>
                              handleMouseDown(event, polygonIndex, pointIndex)
                            }
                          >
                            <circle
                              cx={x}
                              cy={y}
                              r={24}
                              fill="transparent"
                              stroke="transparent"
                            />
                            <circle
                              cx={x}
                              cy={y}
                              r={12}
                              fill={polygonColor}
                              strokeWidth={2}
                            />
                          </g>
                        ))}
                    </g>
                  );
                })}
              {/* Drawing polygon preview */}
              {canEdit && pixelDrawingPolygon.length > 0 && (
                <g key="drawing-polygon">
                  {/* Preview line */}
                  {pixelDrawingPolygon.length > 1 && (
                    <polyline
                      points={pixelDrawingPolygon
                        .map(([x, y]) => `${x},${y}`)
                        .join(' ')}
                      fill="none"
                      stroke={polygonColor}
                      strokeWidth={8}
                      strokeDasharray="10,10"
                      opacity={0.7}
                    />
                  )}

                  {/* Preview points */}
                  {pixelDrawingPolygon.map(([x, y], index) => (
                    <circle
                      key={`drawing-point-${index}`}
                      cx={x}
                      cy={y}
                      r={14}
                      fill={polygonColor}
                      stroke="white"
                      strokeWidth={2}
                      opacity={0.8}
                    />
                  ))}
                </g>
              )}
            </svg>
          )}
        </div>
      </div>
    );
  },
);
